import type { Paragraph, TextLine } from '@/api/types/stowed-element'
import { mergeAdjacentRuns } from '@/lib/text/text-style-engine'

/**
 * Two seam primitives, both consumed by `chain-story.ts`.
 *
 * **The filename is now wider than its contents.** `mergeOverflowIntoExisting` — the "merge this
 * frame's overflow into the next frame's existing content" step this module was named for — was
 * deleted in Phase C of the one-story refactor (`docs/chain-one-story.md`). There is no hand-off
 * merge left to do: a chain is one story, and `distributeStory` re-splits it rather than shuttling
 * blocks between frames. What survives is the seam arithmetic that step needed, which the story
 * assembler needs for the opposite reason — rejoining a paragraph a previous split cut in half.
 *
 * Phase D then removed `mergeParagraphAtSeam`'s `metadataFrom` parameter, so neither export takes a
 * decision about whose formatting survives any more. Both are now pure functions of the text.
 */

/**
 * Whether `paragraphs` holds any actual text, as opposed to only style-carrying blank paragraphs
 * (a frame's style seed — `buildLinkedFrameStyleSeed` — or a frame the user emptied by deleting
 * every character, which is kept as one blank paragraph rather than `[]` precisely so its font
 * survives).
 *
 * Deliberately a plain model walk, not `hasShapeTextContent` — that lives in the shape stack, which `chain-story.ts` keeps no
 * dependency on (the `fit` callback is the only seam), and it treats whitespace-only as empty. A run
 * holding just a space is real text the user typed, so it counts here: callers use this to decide
 * whether content may be DISCARDED, and that must err toward keeping it.
 */
export function paragraphsHaveText(paragraphs: Paragraph[]): boolean {
  for (const paragraph of paragraphs) {
    for (const line of paragraph.textLines ?? []) {
      for (const run of line.textRuns ?? []) {
        if ((run.textContent ?? '').length > 0) return true
      }
    }
  }

  return false
}

/**
 * Merges the seam between two halves of a split paragraph, combining `overflowPara`'s LAST line with
 * `existingPara`'s FIRST line so the two halves render as one continuous line of text (concatenating
 * textRuns via `mergeAdjacentRuns` — not a soft line break). Other lines on either side stay as
 * separate lines within the merged paragraph.
 *
 * Its one caller is `fuseLists` in `chain-story/helpers/seam-probe.ts`, which uses it to rejoin a mid-paragraph seam
 * while assembling a chain's story: `splitShapeTextParagraphsAtOffset` writes such a split as two
 * `Paragraph` objects, and concatenating them would put a hard break mid-sentence.
 *
 * **The merged paragraph keeps the UPSTREAM half's paragraph-level properties** — alignment,
 * spacing, indentation, drop cap, style — and there is no longer a side to choose (D6). A fused seam
 * is by definition two halves of ONE source paragraph, so the upstream half's properties ARE that
 * paragraph's own and the downstream half's are a copy of them. Formatting travels with the text
 * rather than being overwritten by whichever frame currently displays it.
 *
 * **This reverses the 2026-08-12 ruling** that the destination frame keeps its own paragraph
 * formatting when upstream text joins its first line, and the `metadataFrom` parameter that
 * implemented it is gone. That ruling was right for the engine it was made against, where a seam
 * could join two INDEPENDENT stories at a link boundary and the two halves genuinely disagreed. It
 * cannot happen now: link distributes the two sides as two stories and never fuses across the
 * boundary (D3), so the parameter could only ever be passed the value it already used. Do not re-add
 * it — restoring the choice restores the question of who answers it, which is the class of guess this
 * whole refactor removed.
 */
export function mergeParagraphAtSeam(overflowPara: Paragraph, existingPara: Paragraph): Paragraph {
  const overflowLines = overflowPara.textLines ?? []
  const existingLines = existingPara.textLines ?? []
  // The upstream half owns the paragraph properties, unconditionally (D6). Lines are spliced in
  // document order regardless, so the two are still taken independently — just no longer a choice.
  const meta = overflowPara

  if (overflowLines.length === 0) return { ...meta, textLines: existingLines }
  if (existingLines.length === 0) return { ...meta, textLines: overflowLines }

  const openingLine = overflowLines[overflowLines.length - 1]!
  const closingLine = existingLines[0]!
  const seam: TextLine = {
    // Margins survive the fuse, taken from whichever half owns that edge: the merged line STARTS
    // where the overflow half started and ENDS where the existing half ended. They used to be
    // dropped outright along with every other `TextLine` field, so fusing a seam silently reset a
    // line's `leftMargin`/`rightMargin` (yb360 `LM`) to unset.
    //
    // `textWidth` and `descent` are deliberately NOT carried: they are measured values, recomputed
    // from the merged runs on save, and copying either half's would be a stale number for a line
    // that is now longer than both. `isHidden` is likewise dropped — the fused line is visible if
    // either half was, and a hidden half is not a reason to hide the result.
    ...(openingLine.leftMargin != null ? { leftMargin: openingLine.leftMargin } : {}),
    ...(closingLine.rightMargin != null ? { rightMargin: closingLine.rightMargin } : {}),
    textRuns: mergeAdjacentRuns([...(openingLine.textRuns ?? []), ...(closingLine.textRuns ?? [])]),
  }

  return {
    ...meta,
    textLines: [...overflowLines.slice(0, -1), seam, ...existingLines.slice(1)],
  }
}
